import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays, CheckCircle2, AlertCircle, Plane, Palmtree, Timer, RefreshCw, Users, MoonStar, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";

const SEMUA = "__ALL__";

const KATEGORI = [
  { key: "belum_absen", label: "Belum Absen", icon: AlertCircle, tone: "rose" },
  { key: "sudah_absen", label: "Sudah Absen", icon: CheckCircle2, tone: "emerald" },
  { key: "cuti_izin_sakit", label: "Cuti / Izin / Sakit", icon: Palmtree, tone: "amber" },
  { key: "dinas_tugas_luar", label: "Dinas / Tugas Luar", icon: Plane, tone: "sky" },
  { key: "lembur", label: "Lembur Hari Ini", icon: Timer, tone: "indigo" },
  { key: "libur_jadwal", label: "Libur Jadwal", icon: MoonStar, tone: "slate" },
];

const TONES = {
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function AdminSummaryHarian() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState(SEMUA);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("belum_absen");
  const [kontrakSoon, setKontrakSoon] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      // Admin bebas memilih; Atasan Unit di-scoping otomatis di backend.
      if (isAdmin && unitId !== SEMUA) params.unit_id = unitId;
      const callers = [
        api.get("/summary/harian", { params }),
        units.length ? Promise.resolve({ data: units }) : api.get("/master/unit"),
      ];
      if (isAdmin) callers.push(api.get("/pegawai/kontrak-akan-habis"));
      const results = await Promise.all(callers);
      const s = results[0];
      const u = results[1];
      setData(s.data);
      if (!units.length) setUnits(u.data);
      if (isAdmin && results[2]) setKontrakSoon(results[2].data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId]);

  const totals = data?.totals || {};
  const rows = data?.kategori?.[tab] || [];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-summary-harian">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            {isAdmin ? "Administrasi" : "Laporan Absensi"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            {isAdmin ? "Summary Harian" : "Summary Harian Tim"}
          </h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
            <CalendarDays size={13} /> {data?.tanggal_label || "—"}
            {data?.hari_libur ? (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100">
                Libur: {data.hari_libur.nama}
              </span>
            ) : null}
          </p>
          {!isAdmin ? (
            <p className="text-xs text-slate-500 mt-1.5">
              Menampilkan status kehadiran hari ini untuk staf di Unit Kerja Anda.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="w-full sm:w-56">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit Kerja</Label>
            {isAdmin ? (
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger className="mt-1.5" data-testid="summary-unit-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEMUA}>Semua Unit</SelectItem>
                  {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            ) : (
              <div
                className="mt-1.5 h-10 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600"
                data-testid="summary-unit-locked"
              >
                {units.find((u) => u.id === user?.unit_id)?.nama || "Unit Kerja Anda"}
              </div>
            )}
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="summary-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="summary-stat-pegawai">
          <div className="w-9 h-9 rounded-lg border bg-teal-50 text-teal-700 border-teal-200 flex items-center justify-center">
            <Users size={16} />
          </div>
          <div className="mt-3 text-2xl font-extrabold text-slate-900 leading-none">{totals.pegawai ?? 0}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">Total Pegawai</div>
        </div>
        {KATEGORI.map((k) => (
          <button
            key={k.key}
            onClick={() => setTab(k.key)}
            className={`text-left rounded-xl border bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-sm ${
              tab === k.key ? "border-teal-300 ring-1 ring-teal-200" : "border-slate-200"
            }`}
            data-testid={`summary-stat-${k.key}`}
          >
            <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${TONES[k.tone]}`}>
              <k.icon size={16} />
            </div>
            <div className="mt-3 text-2xl font-extrabold text-slate-900 leading-none">{totals[k.key] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">{k.label}</div>
          </button>
        ))}
      </div>

      {isAdmin && kontrakSoon.length > 0 ? (
        <KontrakAlertPanel items={kontrakSoon} />
      ) : null}

      <div className="mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto" data-testid="summary-tabs">
            {KATEGORI.map((k) => (
              <TabsTrigger key={k.key} value={k.key} data-testid={`summary-tab-${k.key}`}>
                {k.label} ({totals[k.key] ?? 0})
              </TabsTrigger>
            ))}
          </TabsList>

          {KATEGORI.map((k) => (
            <TabsContent key={k.key} value={k.key} className="mt-4">
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                {loading ? (
                  <div className="py-12 text-center text-slate-500 text-sm">Memuat…</div>
                ) : rows.length === 0 ? (
                  <div className="py-12 text-center" data-testid={`summary-empty-${k.key}`}>
                    <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <k.icon size={20} />
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-700">Tidak ada pegawai pada kategori ini</div>
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <li key={`${k.key}-${r.user_id}`} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`summary-row-${r.user_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900">{r.nama}</div>
                          <div className="text-[11px] text-slate-500">
                            <span className="font-mono-clock">{r.npp}</span> · {r.unit_nama} · {r.jabatan_nama}
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 sm:text-right">
                          {r.keterangan ? (
                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">
                              {r.keterangan}
                            </span>
                          ) : null}
                          {r.jam_masuk ? (
                            <span className="ml-2 font-mono-clock">
                              Masuk {r.jam_masuk}
                              {r.check_in_status === "Terlambat" ? (
                                <span className="ml-1 text-rose-700 font-semibold">(Terlambat)</span>
                              ) : null}
                              {r.jam_keluar ? ` · Keluar ${r.jam_keluar}` : ""}
                            </span>
                          ) : null}
                          {r.rencana_mulai ? (
                            <span className="ml-2 font-mono-clock">
                              {r.rencana_mulai}–{r.rencana_selesai} · {r.lembur_status}
                            </span>
                          ) : null}
                          {!r.keterangan && !r.jam_masuk && !r.rencana_mulai ? (
                            <span className="text-slate-400">{r.is_hari_kerja ? "Belum check-in" : "Bukan hari kerja"}</span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

function KontrakAlertPanel({ items }) {
  const [open, setOpen] = useState(true);
  const sudahLewat = items.filter((x) => x.sudah_lewat);
  const akanHabis = items.filter((x) => !x.sudah_lewat);
  return (
    <div
      className="mt-5 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden"
      data-testid="dashboard-kontrak-alert"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-100/50 transition-colors"
      >
        <AlertTriangle size={18} className="text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-amber-900 text-sm">
            {items.length} pegawai: kontrak akan habis dalam ≤ 30 hari
          </div>
          <div className="text-[11px] text-amber-800">
            {akanHabis.length} akan habis · {sudahLewat.length} sudah lewat jatuh tempo
          </div>
        </div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
          {open ? "Sembunyikan" : "Tampilkan"}
        </div>
      </button>
      {open ? (
        <div className="border-t border-amber-200 bg-white/50 divide-y divide-amber-100">
          {items.slice(0, 50).map((x) => (
            <div
              key={x.user_id}
              className="px-4 py-2.5 flex items-center gap-3 hover:bg-amber-100/40"
              data-testid={`dashboard-kontrak-row-${x.user_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{x.name}</div>
                <div className="text-[11px] text-slate-500 font-mono-clock">{x.npp}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-700 font-mono-clock">{x.akhir_kontrak}</div>
                <div
                  className={`text-[11px] font-bold ${
                    x.sudah_lewat ? "text-rose-700" : x.sisa_hari <= 7 ? "text-rose-700" : "text-amber-800"
                  }`}
                >
                  {x.sudah_lewat ? `Lewat ${Math.abs(x.sisa_hari)} hari` : x.sisa_hari === 0 ? "Berakhir hari ini" : `${x.sisa_hari} hari lagi`}
                </div>
              </div>
            </div>
          ))}
          {items.length > 50 ? (
            <div className="px-4 py-2.5 text-[11px] text-amber-800 italic text-center">
              + {items.length - 50} pegawai lainnya tidak ditampilkan — buka Master Pegawai untuk daftar lengkap.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

